import { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/lib/auth';
import { getQuizConfig, submitQuiz } from '@/lib/profile';
import { ChevronLeft, ChevronRight, Check, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QuizConfigV2, QuizQuestionV2 } from '@/types/profile';
import { computeActiveQuestions } from './branching';

// ----- Legacy v1 config shape (kept for back-compat when quiz_config has no tiers) -----
interface QuizQuestionV1 {
  id: string;
  question: string;
  options: Array<{
    id: string;
    text: string;
    category: string;
    weight: number;
  }>;
  order: number;
}

// DB shape: `quiz_config` table has `questions` jsonb. For v2 we store the
// tiered object inside that column; v1 stores a flat array.
type RawQuizConfig = {
  id: string;
  version?: string;
  questions: QuizQuestionV1[] | QuizConfigV2;
};

interface TravelQuizProps {
  onComplete: (result: unknown) => void;
  onCancel: () => void;
  existingResult?: unknown;
}

function isV2Config(cfg: RawQuizConfig | null | undefined): cfg is RawQuizConfig & { questions: QuizConfigV2 } {
  const q = cfg?.questions;
  return !!q && !Array.isArray(q) && typeof q === 'object' && 'tiers' in q;
}

export function TravelQuiz({ onComplete, onCancel }: TravelQuizProps) {
  const { user } = useAuth();
  const [rawConfig, setRawConfig] = useState<RawQuizConfig | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    loadQuizConfig();
  }, []);

  const loadQuizConfig = async () => {
    try {
      const config = await getQuizConfig();
      if (config && config.questions) {
        setRawConfig(config as RawQuizConfig);
      } else {
        setRawConfig(getMockV1Config());
      }
    } catch (error) {
      console.error('Error loading quiz config:', error);
      setRawConfig(getMockV1Config());
    } finally {
      setLoading(false);
    }
  };

  const v2Config: QuizConfigV2 | null = isV2Config(rawConfig) ? (rawConfig!.questions as QuizConfigV2) : null;

  // V2 branching: active question list recomputes whenever answers change.
  const activeQuestions: QuizQuestionV2[] = useMemo(() => {
    if (v2Config) return computeActiveQuestions(answers, v2Config);
    return [];
  }, [v2Config, answers]);

  // V2: prune answers for questions that are no longer active (e.g. user
  // switched their core answer and a branch got dropped).
  //
  // PR#263 review: `answers` is intentionally excluded from the deps — this
  // effect *writes* to `answers` via setAnswers, so including it would feed
  // the write back into the input and loop. The activeQuestions list is
  // already derived from `answers` via useMemo, so any relevant change
  // re-fires this effect through the activeQuestions dep.
  useEffect(() => {
    if (!v2Config) return;
    const activeIds = new Set(activeQuestions.map(q => q.id));
    const filtered = Object.fromEntries(Object.entries(answers).filter(([qid]) => activeIds.has(qid)));
    if (Object.keys(filtered).length !== Object.keys(answers).length) {
      setAnswers(filtered);
      // Clamp question index if it now points past the end
      setCurrentQuestionIndex(idx => Math.min(idx, activeQuestions.length - 1));
    }
  }, [activeQuestions, v2Config]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legacy v1 question list
  const v1Questions: QuizQuestionV1[] = useMemo(() => {
    if (v2Config || !rawConfig) return [];
    const arr = rawConfig.questions as QuizQuestionV1[];
    return [...arr].sort((a, b) => a.order - b.order);
  }, [rawConfig, v2Config]);

  const totalQuestions = v2Config ? activeQuestions.length : v1Questions.length;
  const currentQuestion = v2Config
    ? activeQuestions[currentQuestionIndex]
    : v1Questions[currentQuestionIndex];

  const handleAnswerSelect = (questionId: string, optionId: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) setCurrentQuestionIndex(i => i + 1);
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(i => i - 1);
  };

  const handleSubmit = async () => {
    if (!user || !rawConfig) return;

    setSubmitting(true);
    try {
      let result: unknown;
      if (v2Config) {
        const activeIds = new Set(activeQuestions.map(q => q.id));
        const formatted = Object.entries(answers)
          .filter(([qid]) => activeIds.has(qid))
          .map(([questionId, optionId]) => {
            const question = activeQuestions.find(q => q.id === questionId);
            const option = question?.options.find(o => o.id === optionId);
            return {
              question_id: questionId,
              option_id: optionId,
              question_weight: question?.weight ?? 1,
              dimensions: option?.dimensions ?? {},
              metadata_capture: question?.metadata_capture,
              metadata_value: option?.text,
            };
          });
        result = await submitQuiz(user.id, formatted, activeQuestions);
      } else {
        const formatted = Object.entries(answers).map(([questionId, optionId]) => {
          const question = v1Questions.find(q => q.id === questionId);
          const option = question?.options.find(o => o.id === optionId);
          return {
            question_id: questionId,
            option_id: optionId,
            category: option?.category,
            weight: option?.weight,
          };
        });
        result = await submitQuiz(user.id, formatted);
      }

      if (result) {
        const quizData = { result, completedAt: new Date().toISOString(), userId: user.id };
        localStorage.setItem(`quiz_result_${user.id}`, JSON.stringify(quizData));
      }

      setShowResults(true);
      setTimeout(() => onComplete(result), 2000);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B6FED]" />
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Quiz Complete!</h2>
        <p className="text-muted-foreground">Calculating your travel personality...</p>
      </div>
    );
  }

  if (!currentQuestion || totalQuestions === 0) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground">No quiz questions available.</p>
        <div className="flex justify-center mt-4">
          <Button onClick={onCancel} variant="outline">Go Back</Button>
        </div>
      </Card>
    );
  }

  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const allActiveQuestionIds = v2Config
    ? activeQuestions.map(q => q.id)
    : v1Questions.map(q => q.id);
  const allQuestionsAnswered = allActiveQuestionIds.every(id => answers[id]);

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
          <span>{Math.round(progress)}% Complete</span>
        </div>
        <Progress value={progress} className="h-2" />
        {v2Config && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Questions adapt to your answers
          </p>
        )}
      </div>

      {/* Question Card */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-6">{currentQuestion.question}</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {currentQuestion.options.map(option => (
            <button
              key={option.id}
              onClick={() => handleAnswerSelect(currentQuestion.id, option.id)}
              className={cn(
                'px-6 py-3 rounded-full transition-all text-left font-medium',
                answers[currentQuestion.id] === option.id
                  ? 'bg-[#5B6FED] text-white scale-105'
                  : 'bg-[#E8E8FF] text-[#5B6FED] hover:bg-[#D9D9FF]'
              )}
            >
              {option.text}
            </button>
          ))}
        </div>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={handlePrevious}
            variant="outline"
            disabled={currentQuestionIndex === 0}
            className="rounded-full"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
        </div>

        <div className="flex gap-2">
          {!isLastQuestion ? (
            <Button
              onClick={handleNext}
              disabled={!answers[currentQuestion.id]}
              className="bg-[#5B6FED] hover:bg-[#4A5FDD] text-white rounded-full"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered || submitting}
              className="bg-[#5B6FED] hover:bg-[#4A5FDD] text-white rounded-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Complete Quiz
                  <Check className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Quick Navigation Dots */}
      <div className="flex justify-center gap-2">
        {allActiveQuestionIds.map((qid, index) => (
          <button
            key={qid}
            onClick={() => setCurrentQuestionIndex(index)}
            className={cn(
              'w-2 h-2 rounded-full transition-all',
              index === currentQuestionIndex
                ? 'bg-[#5B6FED] w-6'
                : answers[qid]
                ? 'bg-[#5B6FED]/50'
                : 'bg-gray-300'
            )}
          />
        ))}
      </div>
    </div>
  );
}

// Legacy v1 fallback when quiz_config is empty / unreachable — kept so the
// quiz stays functional if the seed row is missing in a given environment.
function getMockV1Config(): RawQuizConfig {
  return {
    id: 'mock-quiz',
    questions: [
      {
        id: '1',
        question: 'What type of vacation energizes you the most?',
        order: 1,
        options: [
          { id: '1a', text: 'Adventure and outdoor activities', category: 'adventure', weight: 1 },
          { id: '1b', text: 'Cultural immersion and history', category: 'culture', weight: 1 },
          { id: '1c', text: 'Beach relaxation and spa', category: 'relaxation', weight: 1 },
          { id: '1d', text: 'City exploration and nightlife', category: 'urban', weight: 1 },
        ],
      },
      {
        id: '2',
        question: 'How do you prefer to plan your trips?',
        order: 2,
        options: [
          { id: '2a', text: 'Every detail planned in advance', category: 'structured', weight: 1 },
          { id: '2b', text: 'Rough itinerary with flexibility', category: 'flexible', weight: 1 },
          { id: '2c', text: 'Completely spontaneous', category: 'spontaneous', weight: 1 },
          { id: '2d', text: 'Mix of planned and free time', category: 'balanced', weight: 1 },
        ],
      },
      {
        id: '3',
        question: "What's your ideal accommodation style?",
        order: 3,
        options: [
          { id: '3a', text: 'Luxury hotels with full service', category: 'luxury', weight: 1 },
          { id: '3b', text: 'Boutique hotels with character', category: 'boutique', weight: 1 },
          { id: '3c', text: 'Local homestays and B&Bs', category: 'authentic', weight: 1 },
          { id: '3d', text: 'Budget-friendly hostels', category: 'budget', weight: 1 },
        ],
      },
      {
        id: '4',
        question: 'How important is food in your travel experience?',
        order: 4,
        options: [
          { id: '4a', text: 'Central - I plan trips around food', category: 'foodie', weight: 1 },
          { id: '4b', text: 'Important - I seek local cuisines', category: 'culinary', weight: 1 },
          { id: '4c', text: 'Moderate - I enjoy trying new things', category: 'explorer', weight: 1 },
          { id: '4d', text: 'Not priority - I eat to fuel activities', category: 'practical', weight: 1 },
        ],
      },
      {
        id: '5',
        question: 'What pace do you prefer when traveling?',
        order: 5,
        options: [
          { id: '5a', text: 'Fast-paced, see everything possible', category: 'intensive', weight: 1 },
          { id: '5b', text: 'Moderate, mix of activities and rest', category: 'balanced', weight: 1 },
          { id: '5c', text: 'Slow, deep dive into few places', category: 'immersive', weight: 1 },
          { id: '5d', text: 'Relaxed, plenty of downtime', category: 'leisurely', weight: 1 },
        ],
      },
    ],
  };
}
